import { css } from "@emotion/react"
import styled from "@emotion/styled"
import {
	colorPrimary,
	fontPorscheNext,
	fontWeightNormal,
	fontWeightSemibold,
	getFocusVisibleStyle,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import { useMemo } from "react"
import type { ComponentProps } from "react"
import ReactMarkdown from "react-markdown"
import remarkBreaks from "remark-breaks"
import { shouldForwardProp } from "src/common/shouldForwardProp"

interface MarkdownProps {
	/**
	 * Renders list bullets as checkmarks.
	 */
	checkmarks?: boolean

	/**
	 * Additional remark plugins, they run before the default ones.
	 */
	remarkPlugins?: NonNullable<ComponentProps<typeof ReactMarkdown>["remarkPlugins"]>

	/**
	 * Sets margins between common html elements.
	 */
	spacings?: boolean
	/**
	 * Specifies text to be displayed as Markdown.
	 */
	value: string
}

export const Markdown = ({ checkmarks, remarkPlugins = [], spacings, value }: MarkdownProps) => {
	const val = useMemo(
		() =>
			value
				.replace(/<br\s?\/?>/gm, "\n")
				// escape colons in korean dates, e.g. `2020. 4. 1` and `2020. 12. 30.` which otherwise result in a messed up ordered list
				.replace(/\d{2,4}\.\s1?\d\.\s[1-3]?\d\./g, (date) => date.replace(/\./g, "\\.")),
		[value],
	)

	return (
		<MarkdownWrapper $spacings={spacings} $checkmarks={checkmarks}>
			<ReactMarkdown remarkPlugins={[...remarkPlugins, remarkBreaks]}>{val}</ReactMarkdown>
		</MarkdownWrapper>
	)
}

const minFluidWidth = 480
const maxFluidWidth = 1530

// https://www.smashingmagazine.com/2016/05/fluid-typography/
const fluidFontSize = (minSize: number, maxSize: number) => {
	return css`
		font-size: ${minSize / 16}rem;

		@media (min-width: ${minFluidWidth}px) {
			font-size: calc(${minSize / 16}rem + (${maxSize} - ${minSize}) * ((100vw - ${minFluidWidth}px) / (${maxFluidWidth} - ${minFluidWidth})));
			min-height: 0vw; /* Safari in 2020 */
		}

		@media (min-width: ${maxFluidWidth}px) {
			font-size: ${maxSize / 16}rem;
		}
	`
}

const MarkdownWrapper = styled("div", { shouldForwardProp })<{
	$checkmarks?: boolean
	$spacings?: boolean
}>`
	font-family: ${fontPorscheNext};
	font-weight: ${fontWeightNormal};
	font-size: 1rem;
	line-height: 1.5;
	color: ${colorPrimary};

	img {
		width: 100%;
	}

	h1,
	h2,
	h3,
	h4,
	h5,
	h6,
	ul,
	ol,
	p {
		padding: 0;
		margin: 0;
		overflow-wrap: break-word;
	}

	h1 {
		font-family: ${fontPorscheNext};
		font-weight: ${fontWeightSemibold};
		${fluidFontSize(28, 60)};
		line-height: 1.3;
	}

	h2 {
		font-family: ${fontPorscheNext};
		font-weight: ${fontWeightSemibold};
		${fluidFontSize(24, 48)};
		line-height: 1.4;
	}

	h3 {
		font-family: ${fontPorscheNext};
		font-weight: ${fontWeightSemibold};
		${fluidFontSize(20, 36)};
		line-height: 1.5;
	}

	h4,
	h5,
	h6 {
		font-family: ${fontPorscheNext};
		font-weight: ${fontWeightSemibold};
		${fluidFontSize(18, 24)};
		line-height: 1.6;
	}

	a {
		text-decoration: underline;
		color: inherit;
		${getFocusVisibleStyle({ offset: "none" })}
	}

	p {
		line-height: inherit;
		font-size: inherit;
	}

	li {
		margin-inline-start: ${spacingStaticMd};
		list-style-type: square;
		margin-top: ${spacingStaticSm};
	}

	ol > li {
		list-style-type: decimal;
	}

	hr {
		margin: ${spacingStaticMd} 0;
	}

	strong {
		font-weight: ${fontWeightSemibold};
	}

	${(props) =>
		props.$spacings &&
		css`
			h1,
			h2,
			h3,
			h4,
			h5,
			h6,
			ul,
			ol,
			p {
				&:not(:first-child) {
					margin-top: ${spacingStaticMd};
				}
			}
		`}

	${(props) =>
		props.$checkmarks &&
		css`
			& li {
				list-style-image: url("data:image/svg+xml,%3Csvg%20width%3D%2213px%22%20height%3D%229px%22%20viewBox%3D%220%200%2013%209%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%3Cdefs%3E%3Cpolygon%20id%3D%22path-1%22%20points%3D%2215.75%205%2013.6125%205%207.5%2011.1125%205.1375%208.75%203%208.75%207.5%2013.25%22%3E%3C%2Fpolygon%3E%3C%2Fdefs%3E%3Cg%20id%3D%22Symbols%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20id%3D%22check%22%20transform%3D%22translate%28-3.000000%2C%20-5.000000%29%22%3E%3Cmask%20id%3D%22mask-2%22%20fill%3D%22white%22%3E%3Cuse%20xlink%3Ahref%3D%22%23path-1%22%3E%3C%2Fuse%3E%3C%2Fmask%3E%3Cuse%20id%3D%22Mask%22%20fill%3D%22%23000000%22%20xlink%3Ahref%3D%22%23path-1%22%3E%3C%2Fuse%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E");
			}
		`}
`
